/**
 * 钱包相关接口
 * 对应 Apipost: 用户端 > 用户钱包
 */

import { http } from "@/http/axios";

// ==================== 类型定义 ====================

/** 银行列表项 */
export interface BankItem {
  code: string; // 银行编码 ICBC/ABC/CMB...
  name: string; // 银行名称 中国工商银行
}

/** 提现账户 */
export interface WithdrawAccount {
  id: number;
  uid: number;
  bank_name: string;           // 开户银行
  card_number_masked: string;   // 脱敏卡号 **** **** **** 1234
  cardholder_name: string;      // 持卡人姓名
  reserved_phone_masked: string;// 脱敏手机号 138****5678
  is_default: number;           // 是否默认 0否1是
  created_at: number;           // 创建时间戳
  updated_at: number;           // 更新时间戳
}

// ==================== 钱包详情类型 ====================

/** 钱包概览信息 */
export interface WalletInfo {
  uid: number;
  /** 总余额 */
  balance: number;
  /** 冻结总额（含押金） */
  freeze_balance: number;
  /** 冻结押金（固定1000元） */
  deposit_amount: number;
  /** 可提现余额 = available_balance - deposit_amount */
  usable_balance: number;
  /** 可用余额 = balance - freeze_balance */
  available_balance: number;
}

/** 明细类型ID说明：
 * 1=收入-推荐奖励, 2=支出-提现申请, 3=支出-消费,
 * 4=收入-提现失败退回, 5=收入-系统充值, 6=支出-系统扣减
 */
export const DETAIL_TYPE_MAP: Record<number, { type: "收入" | "支出"; label: string }> = {
  1: { type: "收入", label: "推荐奖励" },
  2: { type: "支出", label: "提现申请" },
  3: { type: "支出", label: "消费" },
  4: { type: "收入", label: "提现失败退回" },
  5: { type: "收入", label: "系统充值" },
  6: { type: "支出", label: "系统扣减" },
};

/** 钱包明细项 */
export interface WalletLogItem {
  id: number;
  /** 收支类型：收入 或 支出 */
  type: "收入" | "支出";
  /** 名称 */
  detail_type: string;
  /** 类型ID (1-6) */
  detail_type_id: number;
  /** 金额 */
  amount: number;
  /** 创建时间 */
  create_time: string;
}

/** 钱包明细分页数据 */
export interface WalletLogsData {
  list: WalletLogItem[];
  total: number;
  current_page: number;
  last_page: number;
  per_page: number;
}

/** wallet/detail 完整响应 data */
export interface WalletDetailData {
  wallet: WalletInfo;
  logs: WalletLogsData;
}

/** 提现申请响应 data */
export interface WithdrawApplyResult {
  amount: number;
  bank_name: string;
  status: string;
}

// ==================== 接口调用 ====================

/**
 * 1. 获取国内银行类型列表
 * GET /home/wallet/bank_list
 * 无需登录
 */
export async function fetchBankList(): Promise<BankItem[]> {
  console.log("[API] 获取银行列表:", "/home/wallet/bank_list");

  const res = await http.get<{ code: number; msg: string; data: BankItem[] }>("/home/wallet/bank_list");

  if (res.data.code !== 200) {
    throw new Error(res.data.msg || "获取银行列表失败");
  }

  return res.data.data;
}

/**
 * 2. 获取我的提现账户列表
 * GET /home/wallet/account/list
 * 需登录（http 拦截器自动带 Authorization）
 */
export async function fetchWithdrawAccountList(): Promise<WithdrawAccount[]> {
  console.log("[API] 获取提现账户列表:", "/home/wallet/account/list");

  const res = await http.get<{
    code: number;
    msg: string;
    data: {
      list: WithdrawAccount[];
      total: number;
    };
  }>("/home/wallet/account/list");

  if (res.data.code !== 200) {
    throw new Error(res.data.msg || "获取提现账户失败");
  }

  return res.data.data.list;
}

/**
 * 3. 新增提现账号
 * POST /home/wallet/account/create (urlencoded)
 * @param params - 表单参数
 */
export async function createWithdrawAccount(params: {
  bank_name: string;       // 开户银行(必填)
  card_number: string;     // 银行卡号10-30位纯数字(必填)
  cardholder_name: string; // 持卡人姓名(必填)
  reserved_phone?: string; // 预留手机号11位(选填)
  is_default?: number;     // 是否默认0否1是(选填)
}): Promise<number> {
  console.log("[API] 新增提现账号:", "/home/wallet/account/create", params);

  const res = await http.post(
    "/home/wallet/account/create",
    new URLSearchParams({
      bank_name: params.bank_name,
      card_number: params.card_number,
      cardholder_name: params.cardholder_name,
      ...(params.reserved_phone ? { reserved_phone: params.reserved_phone } : {}),
      ...(params.is_default !== undefined ? { is_default: String(params.is_default) } : {}),
    }),
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    }
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.msg || "添加提现账号失败");
  }

  return res.data.data?.id;
}

/**
 * 4. 修改提现账户信息
 * POST /home/wallet/account/update (urlencoded)
 */
export async function updateWithdrawAccount(params: {
  id: number;              // 账户ID(必填)
  bank_name?: string;      // 开户银行(必填)
  card_number?: string;    // 银行卡号(必填)
  cardholder_name?: string;// 持卡人姓名(必填)
  reserved_phone?: string; // 预留手机号(选填)
}): Promise<void> {
  console.log("[API] 修改提现账户:", "/home/wallet/account/update", params);

  const res = await http.post(
    "/home/wallet/account/update",
    new URLSearchParams(
      Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
      )
    ),
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    }
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.msg || "修改提现账户失败");
  }
}

/**
 * 5. 设置默认提现账户
 * POST /home/wallet/account/set-default (urlencoded)
 */
export async function setDefaultWithdrawAccount(id: number): Promise<void> {
  console.log("[API] 设置默认提现账户:", "/home/wallet/account/set-default", { id });

  const res = await http.post(
    "/home/wallet/account/set-default",
    new URLSearchParams({ id: String(id) }),
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    }
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.msg || "设置默认账户失败");
  }
}

/**
 * 6. 删除提现账户
 * POST /home/wallet/account/delete (urlencoded)
 */
export async function deleteWithdrawAccount(id: number): Promise<void> {
  console.log("[API] 删除提现账户:", "/home/wallet/account/delete", { id });

  const res = await http.post(
    "/home/wallet/account/delete",
    new URLSearchParams({ id: String(id) }),
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    }
  );

  if (res.data.code !== 200) {
    throw new Error(res.data.msg || "删除提现账户失败");
  }
}

// ==================== 钱包详情 + 提现 ====================

/**
 * 7. 获取钱包详情（余额 + 明细列表）
 * GET /home/wallet/detail
 * @param params - 查询参数
 */
export async function getWalletDetail(params?: {
  page?: number;
  limit?: number;
  /** 明细类型筛选：0=全部(默认), 1=收入, 2=支出 */
  log_type?: 0 | 1 | 2;
  /** 时间戳游标，首次传0，翻页用上一页最后一条的create_time */
  before_ts?: number;
}): Promise<WalletDetailData> {
  console.log("[API] 获取钱包详情:", "/home/wallet/detail", params);

  const queryParams = new URLSearchParams({
    page: String(params?.page ?? 1),
    limit: String(params?.limit ?? 15),
    log_type: String(params?.log_type ?? 0),
    before_ts: String(params?.before_ts ?? 0),
  });

  const res = await http.get<{
    code: number;
    message: string;
    data: WalletDetailData;
  }>(`/home/wallet/detail?${queryParams.toString()}`);

  if (res.data.code !== 200 || !res.data.data) {
    throw new Error(res.data.message || "获取钱包详情失败");
  }

  return res.data.data;
}

/**
 * 8. 申请提现
 * POST /home/wallet/withdraw/apply（form-data）
 * @param amount - 提现金额（必填）
 * @param bank_account_id - 提现账户ID（必填）
 */
export async function applyWithdraw(
  amount: number,
  bank_account_id: number,
): Promise<WithdrawApplyResult> {
  console.log("[API] 申请提现:", "/home/wallet/withdraw/apply", { amount, bank_account_id });

  const formData = new FormData();
  formData.append("amount", String(amount));
  formData.append("bank_account_id", String(bank_account_id));

  const res = await http.post<{
    code: number;
    message: string;
    data: WithdrawApplyResult;
  }>("/home/wallet/withdraw/apply", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  if (res.data.code !== 200) {
    throw new Error(res.data.message || "提现申请失败");
  }

  return res.data.data;
}
